import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useCallback } from 'react'
import { getAuthStatus, login as loginApi, logout as logoutApi } from '../api/auth'
import { queryKeys } from '../lib/query-client'
import { ApiError } from '../api/client'

/**
 * Authentication hook using TanStack Query
 *
 * Features:
 * - Checks auth status on mount
 * - Provides login/logout mutations
 * - Caches auth status to avoid redundant checks
 * - Invalidates cache on login/logout
 */
export function useAuth() {
  const queryClient = useQueryClient()

  // Query auth status
  const {
    data: authStatus,
    isLoading,
    isError,
    error,
  } = useQuery({
    queryKey: queryKeys.auth.status(),
    queryFn: getAuthStatus,
    staleTime: 1000 * 60 * 5, // 5 minutes
    retry: false, // Don't retry auth checks
  })

  // Login mutation
  const loginMutation = useMutation({
    mutationFn: (password: string) => loginApi(password),
    onSuccess: () => {
      // Invalidate auth status to refetch
      queryClient.invalidateQueries({ queryKey: queryKeys.auth.all })
    },
  })

  // Logout mutation
  const logoutMutation = useMutation({
    mutationFn: logoutApi,
    onSuccess: () => {
      // Clear auth cache and mark as unauthenticated
      queryClient.setQueryData(queryKeys.auth.status(), {
        authenticated: false,
      })
      // Invalidate all queries (user data should be cleared)
      queryClient.clear()
    },
  })

  const login = useCallback(
    async (password: string) => {
      try {
        await loginMutation.mutateAsync(password)
        return { success: true, error: null }
      } catch (err) {
        const message = err instanceof ApiError
          ? err.message
          : 'Login failed'
        return { success: false, error: message }
      }
    },
    [loginMutation]
  )

  const logout = useCallback(async () => {
    try {
      await logoutMutation.mutateAsync()
      return { success: true }
    } catch {
      return { success: false }
    }
  }, [logoutMutation])

  return {
    isAuthenticated: authStatus?.authenticated ?? false,
    isLoading,
    isError,
    error: error instanceof ApiError ? error.message : null,
    user: authStatus?.user ?? null,
    login,
    logout,
    isLoggingIn: loginMutation.isPending,
    isLoggingOut: logoutMutation.isPending,
  }
}
